import { useState } from "react";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { useDatasheetSizeRows, useDatasheetTypes, type DatasheetSizeRow } from "@/lib/datasheets";

const PAGE_SIZE = 200;

/// Read-only diagnostic list — replaces legacy ALL_PrepaidItemSizesView. To change a size, open
/// the parent datasheet; there is deliberately no edit affordance here.
export function DatasheetSizesPage() {
  const [typeCode, setTypeCode] = useState("");
  const [hasRemaining, setHasRemaining] = useState(false);
  const [page, setPage] = useState(0);
  const { data: types } = useDatasheetTypes();
  const { data, isLoading } = useDatasheetSizeRows({
    typeCode: typeCode || undefined,
    hasRemaining: hasRemaining || undefined,
  });
  const rows = data ?? [];

  const cols: DataGridColumn<DatasheetSizeRow>[] = [
    { id: "sheet", header: "Datasheet", accessor: (r) => `#${r.datasheetId}` },
    { id: "company", header: "Company", accessor: (r) => r.companyName ?? "—" },
    { id: "type", header: "Type", accessor: (r) => r.datasheetTypeCode },
    { id: "index", header: "Size", accessor: (r) => String(r.sizeIndex) },
    { id: "desc", header: "Description", accessor: (r) => r.description ?? "—" },
    {
      id: "qty",
      header: "Available",
      accessor: (r) => (
        <span className={r.quantityAvail < 0 ? "text-destructive font-medium" : ""}>{r.quantityAvail}</span>
      ),
    },
    {
      id: "price",
      header: "Unit price ex GST",
      accessor: (r) => (r.unitPriceExGst === null ? "—" : `$${r.unitPriceExGst.toFixed(2)}`),
    },
  ];

  return (
    <ListPage
      title="Datasheet Sizes"
      backTo="/admin"
      filterBar={
        <div className="flex items-center gap-3">
          <select
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={typeCode}
            onChange={(e) => { setTypeCode(e.target.value); setPage(0); }}
            aria-label="Filter by type"
          >
            <option value="">All types</option>
            {(types ?? []).map((t) => (
              <option key={t.datasheetTypeCode} value={t.datasheetTypeCode}>{t.description}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={hasRemaining}
              onChange={(e) => { setHasRemaining(e.target.checked); setPage(0); }}
            />
            Only sizes with stock remaining
          </label>
        </div>
      }
    >
      <DataGrid
        columns={cols}
        rows={rows}
        rowKey={(r) => `${r.datasheetId}-${r.sizeIndex}`}
        totalRows={rows.length}
        pageIndex={page}
        pageSize={PAGE_SIZE}
        loading={isLoading}
        emptyHeadline="No size rows."
        onPageChange={setPage}
      />
    </ListPage>
  );
}
